import type { BoardGraph } from '@shared/board/graph';
import type { BoardNode, Connection } from '@shared/board/types';
import type { GameState, LastMove } from './types';

export interface MovementOption {
  node: BoardNode;
  connectionType: Connection['type'];
}

export interface MovementOptionsConfig {
  allowBacktrack?: boolean;
}

export function getAvailableMovementOptions(
  graph: BoardGraph,
  playerId: string,
  currentNodeId: string,
  lastMoveByPlayer: Map<string, LastMove>,
  config: MovementOptionsConfig = {},
): MovementOption[] {
  const options = graph.getNeighbors(currentNodeId);
  if (config.allowBacktrack === true) return options;

  const previousNodeId = lastMoveByPlayer.get(playerId)?.previousNodeId ?? null;
  if (previousNodeId === null) return options;

  const filtered = options.filter(({ node }) => node.id !== previousNodeId);
  return filtered.length > 0 ? filtered : options;
}

export function getInitialMovementOptions(
  graph: BoardGraph,
  state: GameState,
  playerId: string,
): MovementOption[] {
  const player = state.players.get(playerId);
  if (player == null) return [];
  return getAvailableMovementOptions(graph, playerId, player.nodeId, state.lastMoveByPlayer, {
    allowBacktrack: true,
  });
}

export function getInitialMovementOptionIds(
  graph: BoardGraph,
  state: GameState,
  playerId: string,
): string[] {
  return getInitialMovementOptions(graph, state, playerId).map((option) => option.node.id);
}

export function recordLastMove(
  state: GameState,
  playerId: string,
  previousNodeId: string | null,
): void {
  state.lastMoveByPlayer.set(playerId, { previousNodeId });
}

export function clearLastMove(state: GameState, playerId: string): void {
  recordLastMove(state, playerId, null);
}
